import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'

import {
  CENTER_OF_EXCELLENCE,
  TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC,
} from '@lyrahealth-inc/shared-app-logic/src/features/centerOfExcellence/types'
import { SEARCHER_TYPE_TO_MATCH_FOR } from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/constants'

import { trackEventWithObj } from '../../data/mixpanel'
import { useAppDispatch } from '../../data/storeConfiguration/store'
import { getIsUserInternational } from '../../data/user/userSelectors'
import { handleZeroViewSelect } from '../../features/appointmentDashboard/data/appointmentDashboardActions'
import { TREATMENT_RECOMMENDATIONS_ROUTE } from '../constants/routingConstants'

export interface NavigateToSearchForCareChildProps {
  clickEventName?: string
  clickEventParams?: Dict
}

export const useNavigateToSearchForCareChild = ({
  clickEventName,
  clickEventParams,
}: NavigateToSearchForCareChildProps = {}) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { pathname } = useLocation()

  const isInternationalUser = useSelector(getIsUserInternational)

  const navigateToChildSearchDefault = useCallback(
    ({
      toolkitSubTopic,
      toolkitType,
    }: {
      toolkitSubTopic?: TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC
      toolkitType?: CENTER_OF_EXCELLENCE
    } = {}) => {
      dispatch(
        handleZeroViewSelect({
          whoAmIHereFor: SEARCHER_TYPE_TO_MATCH_FOR.OTHER_CHILD,
          navigate,
          currentLocation: pathname,
          showSummaryView: true,
          toolkitType: toolkitType,
          toolkitSubTopic: toolkitSubTopic,
        }),
      )
    },
    [dispatch, navigate, pathname],
  )

  const navigateToChildSearchInternational = useCallback(() => {
    navigate(TREATMENT_RECOMMENDATIONS_ROUTE, { state: { matchFor: SEARCHER_TYPE_TO_MATCH_FOR.OTHER_CHILD } })
  }, [navigate])

  const navigateToChildSearch = useCallback(
    ({
      toolkitSubTopic,
      toolkitType,
    }: {
      toolkitSubTopic?: TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC
      toolkitType?: CENTER_OF_EXCELLENCE
    } = {}) => {
      if (clickEventName) {
        dispatch(
          trackEventWithObj({
            event: clickEventName,
            ...clickEventParams,
          }),
        )
      }
      if (!isInternationalUser) {
        navigateToChildSearchDefault({
          toolkitType,
          toolkitSubTopic,
        })
      } else {
        navigateToChildSearchInternational()
      }
    },
    [
      clickEventName,
      clickEventParams,
      dispatch,
      isInternationalUser,
      navigateToChildSearchDefault,
      navigateToChildSearchInternational,
    ],
  )

  return useMemo(() => {
    return {
      /** Handles if international or not */
      navigateToChildSearch,
      /** Non international */
      navigateToChildSearchDefault,
      /** International only */
      navigateToChildSearchInternational,
    }
  }, [navigateToChildSearch, navigateToChildSearchDefault, navigateToChildSearchInternational])
}
